import {
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import {
  Button,
  Drawer,
  Form,
  InputNumber,
  Modal,
  Space,
  Typography,
  Tooltip,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  applyRewardToLineitem,
  changeWalletPreferance,
  charge,
  completeOrder,
  getOrderTransactions,
  removeAllRewards,
  removeLineitemReward,
  removeVoucher,
  updateOrderPaymentStatus,
  updateTip,
} from "services/booking.service";
import { checkIfRewardIsApplied, getRewardsText } from "utils/common";
import { openNotificationWithIcon } from "utils/Notification";
import RewardPopup from "components/RewardPopup/RewardPopup";
import Tag from "components/Tag/Tag";
import Spinner from "components/Spinner/Spinner";
import PaymentInfoRow from "./PaymentInfoRow";
import WalletSelectPopup from "components/WalletSelectPropup.js";

import styles from "./OrderPaymentDrawer.module.scss";
import AdjustmentsForm from "./AdjustmentsForm";
import TransactionsHistory from "./TransactionsHistory";
import PartialPayments, { SPLIT } from "components/PartialPayments";
import { EXCLUSION_TYPE, MERCHANT } from "utils/enums";
import { setLoading } from "redux/actions/app.actions";
import Signature, { useSignature } from "components/Signature/Signature";
import { generateProfilePDF, getFileDownloadUrl } from "services/guests.services";
import QrGenerator from "./QrGenerator";

const { Title, Link } = Typography;

export default function OrderPaymentDrawer({
  visible,
  onClose,
  order,
  selectedPaymentMethod,
  onSuccess,
  setPaymentVisible,
  isRewardsLoading,
  refreshCustomerRewards,
  rewards,
  availedRewards = [],
  willHostPay,
  customer,
  credit,
  closeCompletely,
  isCreditLoading,
  customerWallets,
  isCustomerWalletsLoading,
  refreshCustomerWallets,
  refreshOrder,
  onPaymentMethodUpdate,

  paymentMethods,
  fetchingPaymentMethods,
  refetchPaymentMethods,
  manageRewardsDisabled
}) {
  const [form] = Form.useForm();
  const [showRewardPopup, setShowRewardPopup] = useState(false);
  const [showSelectWalletPopup, setShowSelectWalletPopup] = useState({
    visible: false,
    walletId: undefined,
    applicableWallets: [],
  });
  const isCashChecked = Form.useWatch("payment", form);
  const useWallet = order?.useWallet;
  const isOrderCancelled = order?.orderStatus === 2;

  const afterInitialLoad = React.useRef(false);
  const customTipBtnRef = React.useRef(null);
  const [signature, getSignatureValue, clearSignatureValue, setSignatureValue] =
    useSignature("orderPaymentModal", form, "signature", false);
  const dispatch = useDispatch();

  const [tip, setTip] = useState(null);

  const [customTip, setCustomTip] = useState(false);
  const isOrderCompleted = order?.orderStatus === 3;
  const total = useMemo(() => {
    let total = 0;
    let newTip = 0;
    for (let i = 0; i < order?.lineItems?.length; i++) {
      total = total + Number(order?.lineItems[i].subTotal ?? 0);
      for (let j = 0; j < order?.lineItems[i].addOns?.length; j++) {
        total = total + Number(order?.lineItems[i].addOns[j].subTotal ?? 0);
      }
    }
    let cost = total;
    // total = total + Number(order?.taxAmount || 0);

    if (order?.goodFaithPolicyFee && order?.goodFaithPolicyFee > 0) {
      total = total + order?.goodFaithPolicyFee;
    }

    if (tip) {
      if (customTip) {
        total = total + tip;
        newTip = tip;
      } else {
        newTip = (total * tip) / 100;
        total = total + newTip;
      }
    }

    if (order?.voucherCode && order?.discount) {
      total = total - (Number(order?.discount) ?? 0);
    }

    let giftcard = 0;
    if (useWallet) {
      giftcard = order?.giftCardAmount;
    }
    total = total - giftcard;

    if (!(isOrderCompleted || isOrderCancelled) && order?.amountPaidUntilNow) {
      total = total - order?.amountPaidUntilNow;
    }

    // total is just the cancellation amount if cancellated
    if (isOrderCancelled) {
      total = order?.cancellationFee ?? 0;
      giftcard = 0;
    }

    return {
      total: Number(total ?? 0),
      cost: Number(cost ?? 0),
      voucher: Number(order?.discount ?? 0),
      tip: Number(newTip ?? 0),
      giftcard: Number(giftcard ?? 0),
      amountPaidUntilNow:
        isOrderCompleted || isOrderCancelled
          ? 0
          : Number(order?.amountPaidUntilNow ?? 0),
      goodFaithPolicyFee: Number(order?.goodFaithPolicyFee) ?? 0,
    };
  }, [
    tip,
    order,
    useWallet,
    credit,
    isOrderCompleted,
    isOrderCancelled,
    customTip,
    order?.giftCardAmount,
  ]);


  const payableAmount = Number(total?.cost ?? 0) + Number(total?.tip ?? 0) +
    Number(total?.taxAmount ?? 0) +
    Number(total?.goodFaithPolicyFee ?? 0) - Number(total?.voucher ?? 0)


  const [splitType, setSplitType] = useState(SPLIT.FULL)
  const [isSplitPayment, setIsSplitPayment] = useState(splitType !== SPLIT.FULL)
  const [customSplitAmount, setCustomSplitAmount] = useState(null);
  const [lockSplitPayment, setLockSplitPayment] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState()

  useEffect(() => {
    async function setDefaultSplitType() {
      const res = await localStorage.getItem("orderSplitTypes")
      if (res) {
        const parsed = await JSON.parse(res);
        const splitType = parsed[order?.orderId]
        if (splitType !== undefined && splitType !== null) {
          setSplitType(splitType)
          setLockSplitPayment(splitType);
          if (splitType === SPLIT.CUSTOM) {
            setCustomSplitAmount(0)
          }

          if (splitType !== SPLIT.FULL) {
            setIsSplitPayment(true)
          } else {
            setIsSplitPayment(false)
          }

        }
      }
    }
    if (visible) {
      setDefaultSplitType()
    }
    else {
      setSplitType(SPLIT.FULL)
      setIsSplitPayment(false)
      setCustomSplitAmount(0)
    }
  }, [visible, order?.orderId])

  async function markOrderComplete() {

    // sanity check
    const customerSignature = checkSignature()
    if (!customerSignature) {
      return
    }

    const res = await dispatch(
      completeOrder(order, {
        tipAmount: total.tip?.toFixed(2) || 0,
        customerSignature
      })
    );
    onSuccess();
  }

  const [transactionsHistory, setTransactionsHistory] = useState({
    data: [],
    isLoading: true
  })

  async function loadTransactions() {
    setTransactionsHistory(state => ({
      ...state,
      isLoading: true
    }))
    const res = await dispatch(getOrderTransactions(order?.orderId))
    if (res?.status === 'success') {
      setTransactionsHistory({
        data: res?.data ?? [],
        isLoading: false
      })
    } else {
      setTransactionsHistory({
        data: [],
        isLoading: false
      })
    }
  }

  useEffect(() => {
    if (visible && order?.orderId) {
      setTransactionsHistory({
        isLoading: true,
        data: []
      })
      loadTransactions()
    } else {
      setTransactionsHistory({
        data: [],
        isLoading: true
      })
    }
  }, [order?.orderId, visible])


  useEffect(() => {
    if (order && Number(order?.tipAmount ?? 0) > 0) {
      setCustomTip(true);
      setTip(Number(order?.tipAmount ?? 0));
      afterInitialLoad.current = true;
    } else {
      setCustomTip(false);
      setTip(null);
      afterInitialLoad.current = true;
    }
  }, [order?.orderId, visible]);

  const onTipChange = (value, e) => {
    const pTip = tip;
    // setCustomTip(false);
    // setTip(null);
    if (pTip !== value) {
      setTip(Number(value) || 0);
    } else {
      setTip(null);
      setCustomTip(false);
    }

    // calculate tip amount
    let tipAmount = 0;

    // if tip value is greater than 0 and not equal to previous tip
    if (value > 0 && value !== pTip) {
      const totalCost = total.cost + total.goodFaithPolicyFee;
      tipAmount = Number(totalCost * (value / 100))?.toFixed(2);
    } else {
      tipAmount = 0;
      customTipBtnRef.current?.focus();
      setCustomTip(false);
    }

    dispatch(
      updateTip(
        order?.orderId,
        tipAmount,
        () => {
          setCustomTip(false);
        },
        (err) => {
          // console.log("erro");
          customTipBtnRef.current?.focus();
          if (Number(order?.tipAmount ?? 0) > 0) {
            setCustomTip(true);
            setTip(Number(order?.tipAmount ?? 0));
          } else {
            setTip(null);
          }
        }
      )
    );
    // dispatch(updateTip(order?.orderId))
  };

  const onCustomTipBlur = (tipAmount) => {
    if (tipAmount == order?.tipAmount) {
      return;
    }

    dispatch(
      updateTip(
        order?.orderId,
        tipAmount,
        () => { },
        (err) => {
          // console.log("erro");
          customTipBtnRef.current?.focus();
          if (Number(order?.tipAmount) > 0) {
            setCustomTip(true);
            setTip(Number(order?.tipAmount ?? 0));
          } else {
            setCustomTip(false);
            setTip(null);
          }
        }
      )
    );
  };

  const deleteConfirmation = (id) => ({
    title: "Remove Reward",
    content: <>Are you sure that you want to remove the reward?</>,
    centered: true,
    maskClosable: true,
    onOk: (close) => {
      dispatch(removeLineitemReward(order?.orderId, id))
      close();
    },
  });

  const isTipDisabled =
    isOrderCompleted || isOrderCancelled || !selectedPaymentMethod;

  const partialPaymentSubmitDisabled = isSplitPayment && (Number(total?.total ?? 0) > 0.1)

  const applyWallet = useCallback((walletId) => {
    setShowSelectWalletPopup({
      visible: false,
      walletId: undefined,
      applicableWallets: [],
    });
    if (!isSplitPayment) {
      dispatch(
        changeWalletPreferance(order?.orderId, true, walletId, refreshOrder, () => {
          form.setFieldValue("useWallet", false);
        })
      );
    } else {
      setPaymentMethod({
        merchant: MERCHANT.Wallet,
        paymentMethodId: walletId
      })
    }

  }, [isSplitPayment, order?.orderId])

  function handleWalletUpdate(walletId) {

    const selectedWallet = customerWallets?.find?.(wallet => wallet.walletId === walletId)
    // We now Allow Rewards if the Wallet is General
    // if there is already a reward attached then show a confirm modal
    if (!selectedWallet?.isGeneral && checkIfRewardIsApplied(order)) {
      Modal.confirm({
        title: "Rewards Applied",
        content:
          "Applying a wallet will remove the rewards. Do you want to proceed?",
        okText: "Yes",
        cancelText: "No",
        onOk: () => {
          // We Now Allow Rewards if the Wallet is General 

          // first call the api to remove the rewards
          dispatch(removeAllRewards(order?.orderId)).then(() => {
            // then change the wallet preference
            applyWallet(walletId);
          });

        },
        maskClosable: true,
        closable: true,
        closeIcon: <CloseCircleOutlined />,
      });
    } else {
      applyWallet(walletId);
    }
  }

  function handleUseWalletChecked() {
    function isWalletApplicable(product, wallet) {
      const hasMatchingProduct =
        wallet.productExcludeType === EXCLUSION_TYPE["All Included"] ||
        (wallet.productExcludeType === EXCLUSION_TYPE["Only Included"] &&
          wallet.productIds.includes(product.productId)) ||
        (wallet.productExcludeType === EXCLUSION_TYPE["All Include Except"] &&
          !wallet.productIds.includes(product.productId));
      return hasMatchingProduct;
    }

    let matchingWallets = [];

    const orderLineItems = [
      order?.lineItems[0],
      ...(order?.lineItems[0]?.addOns ?? []),
    ];

    for (let i = 0; i < customerWallets.length; i++) {
      const wallet = customerWallets[i];
      const orderFacility = order?.facilityId;
      const isPromoWallet = wallet.isGeneral === false;
      const hasBalance = wallet.balance > 0;

      const hasMatchingFacility =
        wallet.facilityExcludeType === EXCLUSION_TYPE["All Included"] ||
        (wallet.facilityExcludeType === EXCLUSION_TYPE["Only Included"] &&
          wallet.facilityIds.includes(orderFacility)) ||
        (wallet.facilityExcludeType === EXCLUSION_TYPE["All Include Except"] &&
          !wallet.facilityIds.includes(orderFacility));

      // go over each line item and match conditions
      let lineItemMatch = false;
      let lineItemsMatchCount = 0;

      for (let j = 0; j < orderLineItems.length; j++) {
        const lineItem = orderLineItems[j];
        lineItemMatch = isWalletApplicable(lineItem, wallet);

        if (lineItemMatch) {
          lineItemsMatchCount++;
          break;
        }
      }


      // let allLineItemsMatch = lineItemsMatchCount === orderLineItems.length;

      if (wallet.isGeneral === true && wallet.balance > 0) {
        matchingWallets.push(wallet);
      } else if (
        isPromoWallet &&
        hasBalance &&
        hasMatchingFacility &&
        lineItemMatch
      ) {
        matchingWallets.push(wallet);
      }
    }

    if (matchingWallets.length === 1) {
      // if there is only one applicable wallet
      // form.setFieldValue("useWallet", false);
      handleWalletUpdate(matchingWallets[0].walletId);
    } else if (matchingWallets.length > 1) {
      // if there are more than 1 applicable wallets
      // form.setFieldValue("useWallet", false);
      setShowSelectWalletPopup({
        visible: true,
        walletId: undefined,
        applicableWallets: matchingWallets,
      });
    } else {
      openNotificationWithIcon(
        "error",
        "No Wallets Available",
        "There is no gift card balance available to be used"
      );
      // form.setFieldValue("useWallet", false);
    }
  }

  function checkSignature() {
    // check signature
    if (!signature) {
      Modal.error({
        title: "Signature Error",
        maskClosable: true,
        content: "Signature Component not loaded. Please reload the page.",
      })
      return
    }
    const signatureValue = getSignatureValue?.()
    if (!signatureValue) {
      Modal.error({
        title: "Signature Error",
        maskClosable: true,
        content: "Customer signature missing.",
      })
      return
    }
    return signatureValue
  }

  async function handleSubmit() {
    //check signature
    if (!checkSignature())
      return


    // check if 
    // const pendingAmount = total?.total;
    const pendingAmount = Number(payableAmount ?? 0);

    let deductingAmount = 0;

    switch (splitType) {
      case SPLIT.FULL:
        deductingAmount = pendingAmount
        break;
      case SPLIT.HALF:
        deductingAmount = pendingAmount / 2
        break;
      case SPLIT.THIRD:
        deductingAmount = pendingAmount / 3
        break;
      case SPLIT.QUATER:
        deductingAmount = pendingAmount / 4
        break;
      case SPLIT.CUSTOM:
        deductingAmount = Number(customSplitAmount ?? 0)
        break;
      default:
        deductingAmount = Number(pendingAmount ?? 0)
        break
    }


    // if for some reason, it happens then the submit is being triggered
    // from the Split Payments, then we just mark the order complete
    if (isSplitPayment) {
      markOrderComplete()
      return
    }


    let result = undefined


    if (paymentMethod.merchant === MERCHANT.Stripe || paymentMethod.merchant === MERCHANT.ApplePay || paymentMethod.merchant === MERCHANT.GooglePay) {
      let res = null
      // this condition to check for possiblibility of having use cash set to true
      if (order?.paymentType === 1) {
        res = await dispatch(updateOrderPaymentStatus(order, 2, undefined, false));
      } else {
        res = { status: 'success' }
      }
      if (res?.status === 'success')
        onPaymentMethodUpdate(paymentMethod.paymentMethodId, markOrderComplete);
    } else if (paymentMethod.merchant === MERCHANT.Cash) {
      const res = await dispatch(updateOrderPaymentStatus(order, 1, undefined, false));
      if (res?.status === 'success') {
        markOrderComplete()
      }
    }


  }

  async function handleCharge(paymentMethod) {
    const pendingAmount = Number(payableAmount ?? 0);

    let deductingAmount = 0;

    switch (splitType) {
      case SPLIT.FULL:
        deductingAmount = pendingAmount
        break;
      case SPLIT.HALF:
        deductingAmount = pendingAmount / 2
        break;
      case SPLIT.THIRD:
        deductingAmount = pendingAmount / 3
        break;
      case SPLIT.QUATER:
        deductingAmount = pendingAmount / 4
        break;
      case SPLIT.CUSTOM:
        deductingAmount = Number(customSplitAmount ?? 0)
        break;
      default:
        deductingAmount = Number(pendingAmount ?? 0)
        break
    }

    let result = undefined

    if (deductingAmount === null || deductingAmount === undefined) {
      openNotificationWithIcon("error",
        "Error!",
        "Charge amount not provided."
      )
      return
    }

    result = await dispatch(
      charge(order?.orderId, {
        chargeAmount: deductingAmount?.toFixed(2),
        merchant: paymentMethod?.merchant,
        paymentMethodId: paymentMethod?.paymentMethodId
      })
    )
    if (deductingAmount >= total?.total && result?.status === 'success') {
      // onSuccess();
      refreshOrder?.()
      refreshCustomerWallets?.()
    } else {

      if (result?.status === 'success') {
        const orderSplitTypes = await localStorage.getItem("orderSplitTypes")
        if (orderSplitTypes) {
          const parsed = JSON.parse(orderSplitTypes)
          parsed[order?.orderId] = splitType
          localStorage.setItem("orderSplitTypes", JSON.stringify(parsed))
        } else {
          const obj = {}
          obj[order?.orderId] = splitType
          localStorage.setItem("orderSplitTypes", JSON.stringify(obj))
        }
        setLockSplitPayment(splitType)
      }
      // just refresh the order in case of simple charge
      refreshOrder?.()
      refreshCustomerWallets?.()
    }
  }

  const isRewardApplied = useMemo(() => {
    return checkIfRewardIsApplied(order)
  }, [order])




  return (
    <Drawer
      size="large"
      width={"min(100vw, 700px)"}
      destroyOnClose
      headerStyle={{ border: "none" }}
      onClose={onClose}
      className="orderPaymentModal"
      title={
        <div className={styles.drawerTitleContainer}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div>
              <Space>
                <div style={{ display: 'flex', gap: 16 }}>
                  <p className={styles.drawerTitle}>Payments</p>
                  {
                    order?.invoiceDocumentId && <Button
                      size="small"
                      shape="round"
                      type="primary"
                      icon={<DownloadOutlined style={{ top: -3, position: 'relative' }} />}
                      onClick={async () => {
                        const report = await dispatch(
                          getFileDownloadUrl(order?.invoiceDocumentId, customer?.customerId, [], true)
                        );
                        if (report?.status === "success") {
                          window.open(report?.data, "_blank");
                        }
                      }}
                      disabled={!customer?.customerId}
                    >Invoice</Button>
                  }

                </div>
                {isOrderCancelled && <Tag className="dangerTag">Cancelled</Tag>}

              </Space>
              <p style={{ marginTop: 12 }}>{`Booking ID: ${order?.orderId}`}</p>

            </div>
            {
              (!isOrderCancelled && !isOrderCompleted) &&
              <QrGenerator
                orderId={order?.orderId}
              />
            }
          </div>
          <CloseOutlined onClick={() => setPaymentVisible(false)} />
        </div>
      }
      placement="right"
      visible={visible}
      footerStyle={{ padding: "24px 24px 0px 24px" }}
      footer={
        <div>
          <div style={{ borderBottom: "2px solid #EFF1F4", paddingBottom: 20 }}>
            <PaymentInfoRow title="Cost" cost={`$${total.cost?.toFixed(2)}`} />
            {order?.voucherCode && (
              <PaymentInfoRow
                title="Voucher"
                cost={
                  `${total.voucher > 0 ? "-" : ""}` +
                  "$" +
                  Math.abs(total.voucher)?.toFixed(2)
                }
              />
            )}
            {tip > 0 && (
              <PaymentInfoRow title="Tip" cost={`$${total.tip?.toFixed(2)}`} />
            )}
            {
              (order?.taxAmount ?? 0) > 0 && <PaymentInfoRow
                title="Tax"
                cost={`$${Number(order?.taxAmount || 0)?.toFixed?.(2)}`}
              />
            }

            {isCreditLoading && (
              <Tooltip title="Loading Gift Card Credits Information">
                <Spinner />
              </Tooltip>
            )}

            {isOrderCancelled && (
              <PaymentInfoRow
                title="Cancellation Fee"
                cost={`$${Number(order?.cancellationFee || 0)?.toFixed(2)}`}
              />
            )}
            {total?.goodFaithPolicyFee > 0 && (
              <PaymentInfoRow
                title="Good Faith Exam Fee"
                cost={`$${total.goodFaithPolicyFee?.toFixed(2)}`}
              />
            )}
            {((useWallet && total?.giftcard > 0) ||
              (isOrderCompleted && total.giftcard > 0)) && (
                <PaymentInfoRow
                  title="Gift Card"
                  cost={`-$${Number(total?.giftcard ?? 0)?.toFixed(2)}`}
                />
              )}
            {total?.amountPaidUntilNow > 0 && (
              <PaymentInfoRow
                title="Amount Paid Until Now"
                cost={`-$${total.amountPaidUntilNow?.toFixed(2)}`}
              />
            )}
            <PaymentInfoRow title="Total" cost={`$${total.total?.toFixed(2)}`} />
          </div>
          <div
            style={{ borderBottom: "2px solid #EFF1F4", padding: "20px 0px" }}
          >
            <div className={styles.footerControlsContainer}>
              <p
                className={styles.backBtn}
                onClick={() => setPaymentVisible(false)}
              >
                Go Back
              </p>
              <Tooltip style={{ display: 'block', width: 240 }}
                title={partialPaymentSubmitDisabled ? `There is pending amount payable: $${Number(total?.total ?? 0).toLocaleString()}` : undefined}

              >
                <Button
                  rounded
                  type={"primary"}
                  style={{ height: 50, borderRadius: 30, width: 240 }}
                  onClick={handleSubmit}
                  disabled={
                    isOrderCancelled ||
                    isOrderCompleted ||
                    !(selectedPaymentMethod || willHostPay)
                    || partialPaymentSubmitDisabled

                  }
                >
                  Submit
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      }
      closable={false}
      bodyStyle={{ paddingTop: 0 }}
    >
      <div>
        <div style={{ borderBottom: "2px solid #EFF1F4", paddingBottom: 20 }}>
          {order?.lineItems?.map((item, id) => {
            let { productName, addOns, rewardId, lineItemId } = item;
            return (
              <div style={{ margin: "12px 0px" }} key={lineItemId}>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <p className={styles.blueHeading}>{`Service ${id + 1}`}</p>
                  <p className={styles.blueHeading}>Amount</p>
                </div>
                <div className={styles.flexSpaceBetween}>
                  <div>
                    <p className={styles.textTitle}>{productName}</p>
                  </div>
                  <div>
                    <p className={styles.textTitle}>
                      ${Number(item.sellingPrice ?? 0)?.toFixed?.(2)}{" "}
                    </p>
                  </div>
                </div>
                {rewardId && (
                  <div className={styles.rewardContainer}>
                    <Space size={"middle"}>
                      <Title
                        level={5}
                        className="tradeGothic"
                        style={{
                          marginBottom: 0,
                          fontSize: 17,
                          color: "#27ca2d",
                        }}
                      >
                        Rewards : {getRewardsText(item)}
                      </Title>
                    </Space>
                    <Space>
                      <Button
                        size="small"
                        shape="circle"
                        danger
                        disabled={isOrderCompleted || isOrderCancelled}
                        icon={
                          <DeleteOutlined
                            style={{
                              color:
                                isOrderCompleted || isOrderCancelled
                                  ? "#000"
                                  : "#ea5961",
                            }}
                          />
                        }
                        onClick={() => {
                          Modal.warning(deleteConfirmation(lineItemId));
                        }}
                      />
                      <span className={styles.discountAmount}>
                        {item?.discountValue > 0 &&
                          ` -$${Number(item?.discountValue ?? 0)?.toFixed?.(
                            2
                          )}`}
                      </span>
                    </Space>
                  </div>
                )}
                {!rewardId && (
                  <Link
                    onClick={() => setShowRewardPopup(item)}
                    underline
                    disabled={
                      isOrderCompleted || isOrderCancelled || manageRewardsDisabled
                    }
                  >
                    Add Reward
                  </Link>
                )}

                <div>
                  {addOns.length
                    ? addOns.map((addon) => {
                      const { rewardId, lineItemId } = addon;
                      return (
                        <>
                          <div className={styles.flexSpaceBetween}>
                            <div>
                              <p className={styles.textTitle}>
                                {addon.productName}
                              </p>
                            </div>
                            <div>
                              <p className={styles.textTitle}>
                                $
                                {Number(addon.sellingPrice ?? 0)?.toFixed?.(
                                  2
                                )}{" "}
                                {/* {addon?.taxAmount > 0 &&
                                    ` + $${Number(
                                      addon?.taxAmount ?? 0
                                    )?.toFixed?.(2)}`} */}
                              </p>
                            </div>
                          </div>
                          {rewardId && (
                            <div
                              className={styles.flexSpaceBetween}
                              style={{
                                marginBottom: 14,
                              }}
                            >
                              <Space size={"middle"}>
                                <Title
                                  level={5}
                                  className="tradeGothic"
                                  style={{
                                    marginBottom: 0,
                                    fontSize: 17,
                                    color: "#27ca2d",
                                  }}
                                >
                                  Rewards : {getRewardsText(addon)}
                                </Title>
                              </Space>
                              <Space>
                                <Button
                                  disabled={
                                    isOrderCompleted || isOrderCancelled
                                  }
                                  size="small"
                                  shape="circle"
                                  danger
                                  icon={
                                    <DeleteOutlined
                                      style={{
                                        color:
                                          isOrderCompleted || isOrderCancelled
                                            ? "#000"
                                            : "#ea5961",
                                      }}
                                    />
                                  }
                                  onClick={() => {
                                    if (isRewardsLoading) {
                                      openNotificationWithIcon(
                                        "error",
                                        "Loading Rewards",
                                        "Please wait while rewards are loading"
                                      );
                                      return;
                                    }
                                    Modal.warning(
                                      deleteConfirmation(lineItemId)
                                    );
                                  }}
                                />
                                <span className={styles.discountAmount}>
                                  {addon?.discountValue > 0 &&
                                    ` -$${Number(
                                      addon?.discountValue ?? 0
                                    )?.toFixed?.(2)}`}
                                </span>
                              </Space>
                            </div>
                          )}
                          {!rewardId && (
                            <Link
                              onClick={() => setShowRewardPopup(addon)}
                              underline
                              disabled={
                                isOrderCompleted ||
                                isOrderCancelled || manageRewardsDisabled
                              }
                            >
                              Add Reward
                            </Link>
                          )}
                        </>
                      );
                    })
                    : null}
                </div>
              </div>
            );
          })}
          {/* <Button style={{ background: '#D8DCE4', height: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', width: 260, fontSize: 16, fontFamily: "tradeGothic", fontWeight: 'bold', borderRadius: 8 }}>+ Add Service or Membership</Button> */}
        </div>

        <div style={{ borderBottom: "2px solid #EFF1F4", padding: "20px 0px" }}>
          <Tooltip
            title={
              isTipDisabled
                ? "A card must be on file for a guest to leave a tip."
                : ""
            }
            placement="topLeft"
          >
            <p className={styles.messageText}>Do you want to leave a tip?</p>

            <div className={styles.tipBtnsContainer}>
              <button
                disabled={isTipDisabled}
                className={styles.tipBtn}
                style={{
                  background: isTipDisabled ? "#f5f5f5" : "#fff",
                  cursor: isTipDisabled ? "not-allowed" : "pointer",
                  borderColor: tip === 15 && !customTip ? "#486BAF" : "#D8DCE4",
                }}
                onClick={() => {
                  if (!isOrderCompleted) onTipChange(15);
                }}
              >
                15%
              </button>
              <button
                disabled={isTipDisabled}
                className={styles.tipBtn}
                style={{
                  background: isTipDisabled ? "#f5f5f5" : "#fff",
                  cursor: isTipDisabled ? "not-allowed" : "pointer",
                  borderColor: tip === 18 && !customTip ? "#486BAF" : "#D8DCE4",
                }}
                onClick={() => {
                  if (!isOrderCompleted) onTipChange(18);
                }}
              >
                18%
              </button>
              <button
                disabled={isTipDisabled}
                className={styles.tipBtn}
                style={{
                  background: isTipDisabled ? "#f5f5f5" : "#fff",
                  cursor: isTipDisabled ? "not-allowed" : "pointer",
                  borderColor: tip === 20 && !customTip ? "#486BAF" : "#D8DCE4",
                }}
                onClick={(e) => {
                  if (!isOrderCompleted) onTipChange(20, e);
                }}
              >
                20%
              </button>
              <button
                disabled={isTipDisabled}
                className={styles.tipBtn}
                style={{
                  background: isTipDisabled ? "#f5f5f5" : "#fff",
                  cursor: isTipDisabled ? "not-allowed" : "pointer",
                  borderColor: customTip ? "#486BAF" : "#D8DCE4",
                }}
                onClick={() => {
                  if (!customTip) {
                    setTip(Number(order?.tipAmount ?? 0));
                    setCustomTip(true);
                  } else {
                    setCustomTip(false);
                    setTip(null);
                    customTipBtnRef.current?.focus();
                    onCustomTipBlur(0);
                  }
                }}
              >
                Custom
              </button>
              {customTip && (
                <InputNumber
                  disabled={isTipDisabled}
                  placeholder="Tip"
                  min={0}
                  value={tip}
                  className={styles.tipInput}
                  // initialValue={order?.voucherCode}
                  onChange={(e) => {
                    setTip(e)
                  }}
                  onBlur={(e) => {
                    // NOTE: we don't want to set the value from e.target.value here
                    // because it is causing string type conversion issues
                    onCustomTipBlur(Number(tip ?? 0));
                  }}
                />
              )}
              <button
                ref={customTipBtnRef}
                style={{
                  border: "none",
                  outline: "none",
                  background: "none",
                  // visibility: "hidden",
                }}
              ></button>
            </div>
          </Tooltip>
        </div>
        <AdjustmentsForm
          order={order}
          form={form}
          customerWallets={customerWallets}
          isCustomerWalletsLoading={isCustomerWalletsLoading}
          credit={credit}
          total={total}
          handleWalletUpdate={handleWalletUpdate}
          setShowSelectWalletPopup={setShowSelectWalletPopup}
          changeWalletPreferance={changeWalletPreferance}
          removeVoucher={removeVoucher}
          handleUseWalletChecked={handleUseWalletChecked}
        />

        {
          <PartialPayments order={order} total={payableAmount}
            splitType={splitType}
            setSplitType={setSplitType}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            customSplitAmount={customSplitAmount}
            setCustomSplitAmount={setCustomSplitAmount}
            defaultPaymentMethod={selectedPaymentMethod}
            paymentMethods={paymentMethods}
            fetchingPaymentMethods={fetchingPaymentMethods}
            refetchPaymentMethods={refetchPaymentMethods}
            handleUseWalletChecked={handleUseWalletChecked}
            wallets={customerWallets}
            isCustomerWalletsLoading={isCustomerWalletsLoading}
            isSplitPayment={isSplitPayment}
            setIsSplitPayment={setIsSplitPayment}
            lockSplitPayment={lockSplitPayment}
            handleSubmit={handleSubmit}
            handleCharge={handleCharge}
            transactionsHistory={transactionsHistory}
            disabled={isOrderCompleted || isOrderCancelled}
          />
        }

        <TransactionsHistory orderId={order?.orderId} data={transactionsHistory?.data ?? []}
          loading={transactionsHistory?.isLoading}
          loadTransactions={loadTransactions}
        />

        {
          !(isOrderCancelled || isOrderCompleted) && <Signature
            signature={signature}
            disabled={isOrderCancelled || isOrderCompleted}
          />
        }

      </div>
      <RewardPopup
        values={showRewardPopup}
        lineItemId={showRewardPopup?.lineItemId}
        rewards={rewards ?? []}
        availedRewards={availedRewards}
        visible={showRewardPopup}
        onCancel={() => setShowRewardPopup(false)}
        onRedeem={(rewardId, lineItemId) => {
          dispatch(
            applyRewardToLineitem(
              order?.orderId,
              showRewardPopup?.lineItemId,
              rewardId,
              () => {
                openNotificationWithIcon(
                  "success",
                  "Success",
                  "Reward applied successfully"
                );

                setShowRewardPopup(false);
              }
            )
          );
        }}
        refreshRewards={refreshCustomerRewards}
        isRewardsLoading={isRewardsLoading}
      />
      <WalletSelectPopup
        wallets={customerWallets}
        isCustomerWalletsLoading={isCustomerWalletsLoading}
        visible={showSelectWalletPopup.visible}
        applicableWallets={showSelectWalletPopup.applicableWallets}
        selectedWalletId={paymentMethod?.merchant === MERCHANT.Wallet ? paymentMethod?.paymentMethodId : undefined}
        onCancel={() => {
          setShowSelectWalletPopup({
            visible: false,
            walletId: undefined,
            applicableWallets: [],
          });
        }}
        onSuccess={handleWalletUpdate}
        isVoucherApplied={!!order?.voucherCode}
        isRewardApplied={isRewardApplied}
      />
    </Drawer>
  );
}
